import { useTranslation } from "@onefront/react-sdk";
import { Stack } from "@vapor/react-material";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BusinessPostDto } from "../../../../../../@types/dtos/business/BusinessPostDto";
import { Feedback } from "../../../../../../@types/enums/general/Feedback";
import { BusinessDetailsForm } from "../../../../../../components/form/business/details";
import WsSnackbar, { WsSnackbarTypes } from "../../../../../../components/ws-snackbar";
import { useGetBusinessList } from "../../../../../../hooks/api/business/useGetBusinessList";
import { usePatchBusiness } from "../../../../../../hooks/api/business/usePatchBusiness";
import { getBodyDiff } from "../../../../../../utils/functions/getBodyDiff";
import { usePatchAdditionalBusinessDetails } from "../../../../../../hooks/api/business/usePatchAdditionalBusinessDetails";

export const BusinessEditDetails = ({
    business,
    isEditing,
    setIsEditing,
    setLoadingSection,
    loading,
    workspaceData,
    fetchBusiness
}) => {
    const { t } = useTranslation();
    const { workspaceId, businessId } = useParams();
    const [bodyRequest, setBodyRequest] = useState<BusinessPostDto>(business);
    const [primaryBusiness, setPrimaryBusiness] = useState<any>(null);
    const [requestType, setRequestType] = useState<any>("MAIN");
    const [usedAtecos, setUsedAtecos] = useState<any>([]);
    const [feedback, setFeedback] = useState<any>(null);

    const { data: businessRes, loading: loadingBusinessList, fetch: getBusinessList } = useGetBusinessList(workspaceId);
    const { loading: loadingPatch, fetch: patchBusiness } = usePatchBusiness(workspaceId, businessId);
    const { loading: loadingPatchAccounting, fetch: patchBusinessAccounting } = usePatchAdditionalBusinessDetails(
        workspaceId,
        businessId,
        "accounting-configuration",
        business?.accountingConfiguration?.id
    );

    const handleChange = useCallback((val, key) => {
        setBodyRequest(prev => ({
            ...prev,
            [key]: val
        }));
    }, []);

    const handleDataChange = useCallback(e => {
        const { value, name } = e.target;

        if (name === "selfEmployed") {
            setBodyRequest(prev => ({
                ...prev,
                [name]: value === "true"
            }));
        } else {
            setBodyRequest(prev => ({
                ...prev,
                [name]: value
            }));
        }
    }, []);

    useEffect(() => {
        if (!loadingBusinessList && businessRes?.usedAtecos?.length > 0) {
            setUsedAtecos(businessRes?.usedAtecos?.filter(ateco => ateco !== business?.atecoCode));
        }
        if (!loadingBusinessList && businessRes?.business?.length > 0) {
            setPrimaryBusiness(businessRes?.business?.find(business => business.type === "MAIN"));
        }
    }, [businessRes, loadingBusinessList, business?.atecoCode]);

    useEffect(() => {
        if (business && !loading) {
            setBodyRequest({ ...business });
        }
    }, [business, loading]);

    useEffect(() => {
        setLoadingSection(loadingBusinessList);
    }, [setLoadingSection, loadingBusinessList]);

    return (
        <Stack sx={{ position: "relative" }}>
            {feedback && (
                <WsSnackbar
                    position={"absolute"}
                    sx={{
                        right: "0 !important",
                        top: "-4.5rem !important",
                        width: 700
                    }}
                    type={feedback === Feedback.ERROR ? WsSnackbarTypes.error : WsSnackbarTypes.success}
                    title={t(`business.details.title`)}
                    message={t(`feedback.business.edit.${feedback}`)}
                    horizontal="right"
                    handleClose={() => setFeedback(null)}
                />
            )}
            <BusinessDetailsForm
                bodyRequest={bodyRequest}
                handleChange={handleChange}
                handleDataChange={handleDataChange}
                isEditing={isEditing}
                isNewBusiness={false}
                prevalentBusiness={primaryBusiness}
                usedAtecos={usedAtecos}
                loading={loadingPatch || loadingBusinessList || loading || loadingPatchAccounting}
                requestType={requestType}
                onSave={() => {
                    const bodyDiff: any = getBodyDiff(bodyRequest, business);
                    setRequestType("CREATE_OR_UPDATE");
                    //TODO: Add call to BFF instead of multiple calls on FE
                    patchBusiness({ data: bodyDiff })
                        .then(res => {
                            if (
                                Object?.keys(bodyDiff)?.includes("selfEmployed") &&
                                business?.accountingConfiguration?.id
                            ) {
                                let simplifiedAccountingType: any =
                                    business?.accountingConfiguration?.simplifiedAccountingType;

                                let vatReportsType = business?.accountingConfiguration?.vatReportsType;

                                if (business?.accountingConfiguration?.accountingType === "ORDINARY") {
                                    simplifiedAccountingType = bodyDiff?.selfEmployed
                                        ? "CASH_ACCOUNTING"
                                        : "PAYMENTS_PRESUMPTION";
                                    vatReportsType = null;
                                } else if (business?.accountingConfiguration?.accountingType === "SIMPLIFIED") {
                                    vatReportsType = bodyDiff.selfEmployed
                                        ? "VAT_REPORTS_WITH_CASH_ANNOTATION"
                                        : vatReportsType;
                                    simplifiedAccountingType = bodyDiff?.selfEmployed
                                        ? "CASH_ACCOUNTING"
                                        : business?.accountingConfiguration?.simplifiedAccountingType;
                                } else if (business?.accountingConfiguration?.accountingType === "FLAT_RATE") {
                                    simplifiedAccountingType = "CASH_ACCOUNTING";
                                    vatReportsType = null;
                                }

                                patchBusinessAccounting({
                                    data: {
                                        accountingType: business?.accountingConfiguration?.accountingType,
                                        simplifiedAccountingType,
                                        vatReportsType
                                    }
                                })
                                    .then(res => {
                                        setFeedback(Feedback.SUCCESS);
                                        setRequestType("MAIN");
                                        setIsEditing(false);
                                        fetchBusiness();
                                    })
                                    .catch(() => setFeedback(Feedback.ERROR));
                            } else {
                                setFeedback(Feedback.SUCCESS);
                                setRequestType("MAIN");
                                setIsEditing(false);
                                fetchBusiness();
                            }
                        })
                        .catch(() => setFeedback(Feedback.ERROR));
                }}
                onEditPrevalent={async () => {
                    setRequestType("EDIT_PREVALENT");
                    await patchBusiness({
                        url: `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/business/${primaryBusiness?.id}`,
                        data: { type: "SECONDARY" }
                    })
                        .then(() => {
                            setFeedback(Feedback.SUBSTITUDE);
                            getBusinessList?.();
                            setBodyRequest(prev => ({
                                ...prev,
                                type: "MAIN"
                            }));
                        })
                        .catch(() => {
                            setFeedback(Feedback.ERROR);
                        });
                }}
                onCancel={() => {
                    setIsEditing(false);
                    setBodyRequest(business);
                }}
                workspaceData={workspaceData}
            />
        </Stack>
    );
};
